import { useTranslation } from 'next-i18next'
import Link from 'next/link'
import { ReactNode } from 'react'
import { Avatar } from 'src/components/Images/Avatar'
import { RedirectUrls } from 'src/types/types'
import { formatTimestampMonthDay } from 'src/utils'
import { IReview, IUser } from '../../types/graphql'
import ReviewStars from './ReviewStars'

// Deterministic — was `toLocaleString('default', { month: 'long' })` which
// uses the runtime default locale (`en-US` on Node, user-locale on Chrome)
// — guaranteed SSR/CSR text mismatch → React #418 on every listing page
// with reviews. See src/utils/index.ts "HYDRATION-SAFE FORMATTERS".
const formatDate = (date: string | undefined | null) =>
  formatTimestampMonthDay(date, {
    year: 'numeric',
    month: 'long',
    day: undefined
  })

const FOCUS_RING =
  'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-800'

/**
 * Neutral disc — no photo, no initials. For a party the page withholds (the
 * anonymized host reply) or never had (the off-platform reviewer). Initials
 * would leak the identity the listing variant removed — see `Monogram`.
 */
function NeutralAvatar({
  icon,
  className
}: {
  icon: string
  className: string
}) {
  return (
    <span
      aria-hidden="true"
      className={`flex shrink-0 items-center justify-center rounded-full border border-ink-100/15 bg-cream-300 text-ink-200 ${className}`}
    >
      <i className={`si ${icon} leading-none`} />
    </span>
  )
}

/** Wraps a byline in a profile link, or in a plain row when there is none. */
function BylineLink({
  href,
  className,
  children
}: {
  href: string | null
  className: string
  children: ReactNode
}) {
  if (!href) return <div className={className}>{children}</div>
  return (
    <Link href={href} className={`group rounded-lg ${FOCUS_RING} ${className}`}>
      {children}
    </Link>
  )
}

const NAME_HOVER =
  'decoration-ink-100/40 underline-offset-2 group-hover:underline'

type IProps = {
  review: IReview
  owner?: IUser | null
  /**
   * `true` = a stacked-list row with no chrome of its own: the reviews modal,
   * the respond modal and the checkout-detail `CheckoutCard` (dashboard) all
   * supply the frame. Omitted = a cell of the editorial two-column grid in
   * `Reviews` (listing + public profile), which draws its own top hairline.
   */
  fullWidth?: boolean
  /**
   * Byline the host reply as "Respuesta del anfitrión" with a neutral avatar
   * and NO link to the host profile. Set on renter-facing listing pages,
   * where the landlord's identity is withheld until checkout — a named,
   * linked reply here would hand back the identity the listing variant just
   * removed. Off on the host's own profile page, where you already know
   * whose page you are on.
   */
  anonymizeOwner?: boolean
}

export default function Review({
  review,
  fullWidth,
  owner,
  anonymizeOwner
}: IProps) {
  const { t } = useTranslation('common')
  const {
    reviewer,
    rating,
    review: reviewText,
    createdAt,
    response,
    respondedAt
  } = review
  // NO `new Date()` fallback. Reviews render on public SSG/ISR listing pages,
  // so defaulting a missing date to "now" baked the build-time month into the
  // cached HTML while the browser rendered the current one → React #418 once
  // the month rolled over (same class as `Anfitrión desde`, hydration_diff
  // 2026-08-05). A review with no date simply shows no date.
  const reviewDate = formatDate(createdAt)
  const responseDate = formatDate(respondedAt)

  // The RESPONSE is written by the host (`owner`), not by the reviewer.
  // `owner` arrives team-first from `getLocationProfile`, so a team-owned
  // listing answers as the operator. Anonymized ⇒ no name, no photo, no link.
  const ownerName = anonymizeOwner
    ? ''
    : [owner?.firstname, owner?.lastname].filter(Boolean).join(' ')
  const ownerPhoto = anonymizeOwner ? undefined : owner?.profilePicture?.url
  const ownerHref =
    !anonymizeOwner && owner?.id ? `${RedirectUrls.User}/${owner.id}` : null
  // One string per node (never `text{' '}{expr}`): this renders on public SSG
  // listing pages, where adjacent text nodes are Trigger H (#418).
  const replyByline = ownerName
    ? t('REVIEWS.REPLY_FROM', { name: ownerName })
    : t('REVIEWS.HOST_REPLY')

  // OFF-PLATFORM renter review (Task 19, spec §9): `ReviewService.getReviews`
  // unions `ClosedDeal.renterReview` rows into this list. Those renters have
  // no SpotMe account, so the row arrives with `reviewer: undefined` BY
  // DESIGN — and hosts/operators are never named on renter-facing surfaces
  // (standing rule), so nothing may be substituted from the deal either. No
  // reviewer ⇒ the neutral label, no profile link (it once rendered
  // `undefined undefined` inside a `/user/undefined` link).
  const reviewerName = reviewer
    ? [reviewer.firstname, reviewer.lastname].filter(Boolean).join(' ')
    : t('REVIEWS.OFFPLATFORM_REVIEWER')
  const reviewerHref = reviewer?.id
    ? `${RedirectUrls.User}/${reviewer.id}`
    : null

  const bodyText = fullWidth ? 'text-[14px]' : 'text-[14px] md:text-[15px]'

  return (
    <article
      className={`min-w-0 ${fullWidth ? 'w-full' : 'border-t border-ink-100/15 py-6'}`}
    >
      {/* -m-1 p-1 grows the tap target to 48px without moving the layout. */}
      <BylineLink
        href={reviewerHref}
        className="-m-1 inline-flex max-w-full items-center gap-3 p-1"
      >
        {reviewer ? (
          <Avatar
            src={reviewer.profilePicture?.url}
            fallbackName={reviewerName}
            alt=""
            sizes="40px"
            className="size-10 shrink-0"
          />
        ) : (
          <NeutralAvatar icon="si-user" className="size-10 text-[16px]" />
        )}
        <span
          className={`min-w-0 text-[14px] leading-5 font-semibold break-words text-ink-700 ${reviewerHref ? NAME_HOVER : ''}`}
        >
          {reviewerName}
        </span>
      </BylineLink>

      <div className="mt-3 flex flex-wrap items-center gap-x-2 gap-y-1 text-[12px] leading-4 text-ink-200">
        <ReviewStars rating={rating ?? 0} />
        {reviewDate && (
          <>
            <span aria-hidden="true">·</span>
            <span>{reviewDate}</span>
          </>
        )}
      </div>

      {reviewText && (
        <p
          className={`mt-2 leading-relaxed break-words text-ink-700 ${bodyText}`}
        >
          {reviewText}
        </p>
      )}

      {response && (
        <div className="mt-4 border-l-2 border-blue-500/40 pl-4">
          <BylineLink
            href={ownerHref}
            className="-m-1 inline-flex max-w-full items-center gap-2.5 p-1"
          >
            {ownerName || ownerPhoto ? (
              <Avatar
                src={ownerPhoto}
                fallbackName={ownerName}
                alt=""
                sizes="28px"
                className="size-7 shrink-0"
              />
            ) : (
              <NeutralAvatar icon="si-house" className="size-7 text-[13px]" />
            )}
            <span className="min-w-0">
              <span
                className={`block text-[13px] leading-5 font-semibold break-words text-ink-700 ${ownerHref ? NAME_HOVER : ''}`}
              >
                {replyByline}
              </span>
              {responseDate && (
                <span className="block text-[12px] leading-4 text-ink-200">
                  {responseDate}
                </span>
              )}
            </span>
          </BylineLink>
          <p
            className={`mt-2 leading-relaxed break-words text-ink-700 ${bodyText}`}
          >
            {response}
          </p>
        </div>
      )}
    </article>
  )
}
